import { useState } from 'react';
import { MoreVertical, Sparkles, AlertTriangle, Trash2, Loader2, Pencil, Info, Copy } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { formatINR, formatRelative, formatNumber, formatDateTime } from '@/lib/format';
import { Transactions as TxnsAPI } from '@/lib/api';
import { formatMode, composeModeValue } from '@/lib/transactionModes';
import { toast } from 'sonner';
import { RewardBreakdownPopover } from './RewardBreakdownPopover';

export const TransactionRow = ({ tx, onDeleted, onEdit }) => {
  const [deleting, setDeleting] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await TxnsAPI.delete(tx.id);
      toast.success('Transaction deleted');
      onDeleted?.(tx.id);
    } catch (e) {
      toast.error(e?.message || 'Failed to delete transaction');
    } finally {
      setDeleting(false);
      setOpenConfirm(false);
    }
  };

  return (
    <div
      data-testid={`transaction-row-${tx.id}`}
      className="flex items-center gap-4 px-4 py-3 hover:bg-[hsl(var(--surface-2))] rounded-lg transition-colors"
    >
      <div className="h-9 w-9 rounded-lg bg-[hsl(var(--surface-3))] grid place-items-center text-sm font-display font-semibold shrink-0">
        {(tx.merchant || '?').charAt(0)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium truncate">{tx.merchant}</span>
          {tx.best ? (
            <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" aria-label="Best card used" />
          ) : (
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" aria-label="Better card available" />
          )}
          {/* Phase 20 — Potential duplicate pill. Surfaces gmail-sync rows
              that share a composite signature with an existing transaction
              or draft. Hover for the matched record details. */}
          {tx.potential_duplicate && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    data-testid={`transaction-row-duplicate-${tx.id}`}
                    className="px-1.5 h-4 text-[10px] gap-1 border-amber-400/60 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                  >
                    <Copy className="h-2.5 w-2.5" aria-hidden />
                    Potential duplicate
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[260px] text-xs leading-snug">
                  <div className="font-medium mb-1">Possible duplicate detected</div>
                  {tx.duplicate_of ? (
                    <div className="text-muted-foreground">
                      Matches a {tx.duplicate_of.kind || 'transaction'}
                      {tx.duplicate_of.date ? ` on ${tx.duplicate_of.date}` : ''}
                      {tx.duplicate_of.amount ? ` for ${formatINR(tx.duplicate_of.amount)}` : ''}
                      {tx.duplicate_of.merchant ? ` at ${tx.duplicate_of.merchant}` : ''}.
                    </div>
                  ) : (
                    <div className="text-muted-foreground">
                      Same amount + card + merchant + hour as an existing entry.
                    </div>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-0.5 flex-wrap">
          {tx.category && (
            <Badge variant="outline" className="px-1.5 py-0 h-4 text-[10px]">{tx.category}</Badge>
          )}
          {/* Phase 20 — show a Mode badge when it's anything other than the
              default "Online". Keeps the row tight when most txns are online. */}
          {composeModeValue(tx) !== 'online' && (
            <Badge
              variant="outline"
              data-testid={`transaction-row-mode-${tx.id}`}
              className="px-1.5 py-0 h-4 text-[10px] border-accent/30 text-foreground/70"
            >
              {formatMode(tx)}
            </Badge>
          )}
          <span className="truncate">{tx.cardName || tx.card_name || ''}</span>
          <span>·</span>
          {/* Phase 20 — render a full date+time when a per-txn timestamp
              exists (gmail-imported rows and post-Phase-20 manual entries);
              fall back to the relative date for legacy rows. */}
          <span data-testid={`transaction-row-when-${tx.id}`}>
            {tx.txn_at ? formatDateTime(tx.txn_at) : formatRelative(tx.date)}
          </span>
        </div>
      </div>
      <div className="text-right shrink-0">
        <div data-testid={`transaction-row-amount-${tx.id}`} className="text-sm font-mono-num font-medium">
          {formatINR(tx.amount)}
        </div>
        {/* Phase 18 (issue #2) — Always show the rewards EARNED on the card
            the user actually charged, regardless of whether it was the best
            card. When the engine found a better alternative we ADDITIONALLY
            show the missed amount on a second line. This gives users a
            "what you got vs what you could've got" comparison on every row.
            Phase 19 (issue #3) — wrapped in a popover trigger so users can
            tap/hover to see the structured breakdown (primary / spillover /
            milestone / tier portions). */}
        <RewardBreakdownPopover
          tx={tx}
          trigger={
            <button
              type="button"
              data-testid={`transaction-row-breakdown-trigger-${tx.id}`}
              className="group inline-flex flex-col items-end gap-0 mt-0.5 rounded-md px-1.5 py-0.5 -mr-1.5 transition-colors hover:bg-[hsl(var(--surface-3))] focus-visible:bg-[hsl(var(--surface-3))] focus-visible:outline-none cursor-pointer"
              aria-label="Show reward breakdown"
            >
              <span
                data-testid={`transaction-row-rewards-${tx.id}`}
                className="text-[11px] font-mono-num text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1"
              >
                +{formatNumber(tx.rewards ?? tx.reward_raw ?? 0)} {((tx.rewardCurrency || tx.reward_currency || '').trim() || 'pts')}
                <Info className="h-3 w-3 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity" aria-hidden />
              </span>
              {!tx.best && (tx.missed ?? tx.missed_inr ?? 0) > 0 && (
                <span
                  data-testid={`transaction-row-missed-${tx.id}`}
                  className="text-[10px] font-mono-num text-amber-600 dark:text-amber-400"
                >
                  Missed {formatINR(tx.missed ?? tx.missed_inr ?? 0)}
                </span>
              )}
            </button>
          }
        />
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full shrink-0"
            aria-label="Transaction actions"
            data-testid={`transaction-row-menu-${tx.id}`}
          >
            <MoreVertical className="h-4 w-4 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          {onEdit && (
            <>
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); onEdit?.(tx); }}
                className="cursor-pointer"
                data-testid={`transaction-row-edit-${tx.id}`}
              >
                <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <AlertDialog open={openConfirm} onOpenChange={setOpenConfirm}>
            <AlertDialogTrigger asChild>
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); setOpenConfirm(true); }}
                className="text-rose-500 focus:text-rose-500 cursor-pointer"
                data-testid={`transaction-row-delete-${tx.id}`}
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
              </DropdownMenuItem>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this transaction?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes <b>{tx.merchant}</b> · <b>{formatINR(tx.amount)}</b> from your log. Rewards and period spend tracking will recompute. This action is not reversible.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid={`transaction-row-delete-cancel-${tx.id}`}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  disabled={deleting}
                  className="bg-rose-500 hover:bg-rose-600 text-white"
                  data-testid={`transaction-row-delete-confirm-${tx.id}`}
                >
                  {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Trash2 className="h-3.5 w-3.5 mr-1.5" />}
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
